import { reactive } from 'vue'

import { isActive } from '@/services/feature-flag-service'
import { httpGetJson } from '@/lib/net'
import { getMode } from '@/lib/modes'
import { getAccessToken } from '@/services/identity-client'
import { addActivityFilter } from '@/services/filter-layer-service'
import { getItem, saveItem } from '@/plugins/storage'

import {
  FISH_ACTIVITY_MODES,
  FISH_LAYER_KEYS,
} from '@/fish/lib/layers/activity-modes'

import { MODES } from '@/fish/lib/enums'

/**
 * Service for modifying, storing, and sharing the map layers
 */
class LayerService {
  /**
   * Initialization function
   */
  constructor() {
    // Set of the app layer collections
    this.collections = {}

    // Set of the app layers. Note that this array will
    // also include layer group so as to keep groups and their
    // children synced.
    this.layers = []

    // Set of active layers on the map. Note that this array will
    // also include layer group id's so as to keep groups and their
    // children synced.
    this.activeLayers = []

    // Set of active collections in Map Layers
    this.activeCollections = {}

    // Hold data for the map layers per mode, per platform, to drive UI updates
    // Ex: Backcountry Trail Mode. Activity ("Hiking"), Layers ("Active Wildfire")
    this.modeMap = {}

    // Holds app preferences
    this.appPreferences = {}

    // Counter for default filter values
    this.defaultCheckCount = new Map()

    // if there was an error requesting the collections from the api
    this.collectionsError = false

    // promise to wait for layers to be ready
    /** @type {(() => void) | null} */
    this.layersReadyResolver = null
    /** @type {Promise<void>} */
    this.layersReadyPromise = new Promise(
      (resolve) => (this.layersReadyResolver = resolve)
    )

    this.layerConfig = null
  }

  /**
   * Store current app preferences for reference outside of Vue components
   */
  updateAppState() {
    this.appPreferences.appName = 'fish'
    this.appPreferences.appMode = getMode()
  }

  /**
   * Get current app name and mode, for use outside Vue components
   * @returns {Object} - Containing current app name and mode
   */
  getAppNameAndMode() {
    this.updateAppState()
    return this.appPreferences
  }

  /**
   * Get the app layer collections
   *
   * @return {Object} - app layer collections
   */
  getCollections() {
    return this.collections
  }

  /**
   * Get a specific collection
   *
   * @param {String} id - collection id
   * @return {Object} - collection object
   */
  getCollection(id) {
    return this.collections[id]
  }

  /**
   * Adds a collection to the full list of collections
   *
   * @param {Object} collection - collection class object
   */
  addCollection(collection) {
    this.collections[collection.id] = collection
  }

  /**
   * Removes a collection from the full list of collections
   *
   * @param {Object} collection - collection class object
   */
  removeCollection(collection) {
    delete this.collections[collection.id]
  }

  /**
   * Get the app layers
   *
   * @return {Array} - app layers
   */
  getLayers() {
    return this.layers
  }

  /**
   * Get a specific app layer
   *
   * @param {string} id - layer id
   * @return {Object} - layer object
   */
  getLayer(id) {
    return this.layers.find((layer) => layer.id === id)
  }

  /**
   * Adds a layer to the full list of layers
   *
   * @param {Object} layer - layer class object
   */
  addLayer(layer) {
    if (!this.layers.includes(layer)) {
      this.layers.push(layer)
    }
  }

  /**
   * Removes a layer from the full list of layers
   *
   * @param {Object} layer - layer class object
   */
  removeLayer(layer) {
    const index = this.layers.indexOf(layer)
    if (index !== -1) {
      this.layers.splice(index, 1)
    }
  }

  /**
   * Returns the app layer ids that are active on the map
   *
   * @return {Array<string>} - app layers
   */
  getActiveLayers() {
    return this.activeLayers
  }

  /**
   * Adds a layer to the list of active layers
   *
   * @param {string|number} - layer id
   */
  addActiveLayer(id) {
    // FUTURE - update list to at some point to manage the draw
    // order on the map
    if (!this.activeLayers.includes(id.toString())) {
      this.activeLayers.push(id.toString())
    }
  }

  /**
   * Removes a layer from the list of active layers
   *
   * @param {string|number} id - layer id
   */
  removeActiveLayer(id) {
    const index = this.activeLayers.indexOf(id.toString())
    if (index !== -1) {
      this.activeLayers.splice(index, 1)
    }
  }

  /**
   * Returns the app collection ids that are active in map layers
   * Specific to Hunt
   *
   * @return {Object} - active collections
   */
  getActiveCollections() {
    return this.activeCollections
  }

  /**
   * Returns the app state collections that are active in map layers
   * Specific to Hunt
   *
   * @return {Array} - active state collections
   */
  getActiveStateCollections() {
    const activeCollections = Object.keys(this.getActiveCollections())
    const stateCollections = []

    for (const collectionId of activeCollections) {
      const collection = this.getCollection(collectionId)

      if (collection.type === 'state') {
        stateCollections.push(collection)
      }
    }

    return stateCollections
  }

  /**
   * Adds a collection to the list of active collections
   * Specific to Hunt
   *
   * @param {Object} - collection
   */
  addActiveCollection(collection) {
    this.activeCollections[collection.id] = collection
  }

  /**
   * Removes a collection from the list of active collections
   * Specific to Hunt
   *
   * @param {Object} collection - collection to be removed
   */
  removeActiveCollection(collection) {
    delete this.activeCollections[collection.id]
  }

  /**
   * Returns the set of layers that can be enabled based on the current
   * mode
   *
   * @returns {array} - mode layer ids
   */
  getModeLayersToEnable() {
    const layersToEnable = []
    this.modeMap = FISH_ACTIVITY_MODES[MODES.FRESHWATER]
    this.setActiveStates(['layers'])
    FISH_LAYER_KEYS.forEach((key) => {
      this.modeMap[key].forEach((item) => {
        layersToEnable.push(item.id)
      })
    })
    return layersToEnable
  }

  /**
   * Certain layers are associated to a mode with no layer facing toggle which means
   * they must always be on for that mode, regardless of default or user preference
   * settings. Return the set of layers that must be enabled based on the current mode.
   *
   * @returns {array} - mode layer ids
   */
  getAlwaysOnModeLayersToEnable() {
    const layersToEnable = []
    this.modeMap = FISH_ACTIVITY_MODES[MODES.FRESHWATER]
    FISH_LAYER_KEYS.forEach((key) => {
      this.modeMap[key]?.forEach((item) => {
        if (item.id && !item.label) {
          layersToEnable.push(item.id)
        }
      })
    })
    return layersToEnable
  }

  /**
   * Get baseline length per mode, to compare to applied filters
   * @returns {Number} - Length of array of activities
   */
  getActivitiesLengthByMode() {
    return this.getModeActivities()?.length ?? 0
  }

  /**
   * Get appropriate layers per mode per platform
   * @returns { Array } - Array of layers in Mode card depending on platform
   */
  getKeyLayers() {
    return [...this.getModeLayers(), ...this.getModeActivities()]
  }

  /**
   * Turn off current layerset on map
   * Used when toggling between mode types
   */
  disableCurrentLayers() {
    const allLayers = this.getKeyLayers()
    allLayers?.forEach((layer) => {
      const serviceLayer = this.getLayer(layer.id)
      layer.active = false
      serviceLayer?.toggleGroup(false)
    })
  }

  /**
   * Turn off current activities and non toggle layersets on map
   * Used when toggling between mode types
   */
  disableModeDrivenLayers() {
    const layers = [
      ...this.getModeActivities(),
      ...(this.modeMap?.nonToggleLayers ?? []),
    ]
    layers?.forEach((layer) => {
      const serviceLayer = this.getLayer(layer.id)
      layer.active = false
      serviceLayer?.toggleGroup(false)
    })
  }

  /**
   * Turn on current layerset on map according to preference
   * Used when toggling between mode types
   */
  enableCurrentLayers() {
    const allLayers = this.getKeyLayers()
    allLayers?.forEach((layer) => {
      const activityLayer = this.getLayer(layer.id)
      if (layer.active) {
        activityLayer?.toggleGroup(true)
      }
    })
  }

  /**
   * Turn on current activity and non toggle layersets on map
   * Used when toggling between mode types
   */
  enableModeDrivenLayers() {
    const layers = [
      ...this.getModeActivities(),
      ...(this.modeMap?.nonToggleLayers ?? []),
    ]
    layers?.forEach((layer) => {
      const serviceLayer = this.getLayer(layer.id)
      if (layer.active) {
        serviceLayer?.toggleGroup(true)
      }
    })
  }

  /**
   * Update the active status of a layer
   * @param {String} id - Layer ID
   */
  updateModeLayer(id) {
    const layer = this.getLayer(id)
    const modeLayer = this.modeMap.layers.find((layer) => layer.id === id)
    if (modeLayer) {
      modeLayer.active = !layer.active
    }
  }

  /**
   * Update existing mode map to contain reactive `active` property
   * @param {Array} keys - Keys from Backcountry/Offroad activity modes
   */
  setActiveStates(keys) {
    keys.forEach((key) => {
      this.modeMap[key] = this.modeMap[key].map((layer) => {
        // Populate active filters
        if (key === 'trails' || key === 'activities') {
          if (this.getLayerPreference(layer.id)?.active) {
            addActivityFilter(layer.id)
          }
        }

        let active = this.getLayerPreference(layer.id)?.active ?? false
        if (layer.id && !layer.label) {
          active = true
        }

        return {
          ...layer,
          active,
          displayActive: active,
          hasAccess: this.getLayer(layer.id)?.hasAccess,
        }
      })
    })
  }

  /**
   * Get all layer objects
   * @returns {Array} - Array of layer objects
   */
  getModeLayers() {
    return [...(this.modeMap?.layers ?? []), ...(this.modeMap?.allLayers ?? [])]
  }

  /**
   * Get an Activity by ID from the array
   * @param {String} id - ID to find object by
   * @returns {Object} Activity object matching ID
   */
  getModeActivity(id) {
    return this.modeMap?.activities.find((item) => item.id === id)
  }

  /**
   * Get all activity objects - Backcountry-specific
   * @returns {Array} - Array of activity objects
   */
  getModeActivities(includeAll = false) {
    if (!this.modeMap?.activities) {
      this.getModeLayersToEnable()
    }
    return includeAll
      ? this.modeMap?.activities
      : this.modeMap?.activities?.filter((act) => act.id !== 'all')
  }

  /**
   * Get specific mode layer
   * @param {string} id - Layer ID to match
   * @returns {object | undefined} Mode layer object matching ID`
   */
  getModeLayer(id) {
    // Sometimes this function can be called before the data has been initialized
    // Make sure to set appropriate layer data before finding
    if (!this.modeMap?.layers) {
      this.getModeLayersToEnable()
    }

    const allLayerGroups = [
      ...(this.modeMap?.layers ?? []),
      ...(this.modeMap?.allLayers ?? []),
      ...(this.modeMap?.conditionsLayers ?? []),
      ...(this.modeMap?.cellCoverageLayers ?? []),
      ...(this.modeMap?.nonToggleLayers ?? []),
    ]
    return allLayerGroups.find((layer) => layer.id === id)
  }

  /**
   * Retrieves a feature gate key using a layer id for layers in a given mode.
   * Note this will only look for layers in the current mode.
   * @param {string} id
   * @returns {string | undefined}
   */
  getModeLayerFeatureKey(id) {
    return this.getModeLayer(id)?.layerGateKey
  }

  /**
   * Get layer objects containing enough data to render
   * @returns {Array} - Array of layer objects
   */
  getFilteredModeLayers() {
    return this.getModeLayers()?.filter((layer) => {
      // Check for any feature gated layers
      if (
        layer.hasOwnProperty('layerGateKey') &&
        !isActive(layer.layerGateKey)
      ) {
        return false
      }

      return layer.img && layer.label
    })
  }

  /**
   * Toggle a layer on or off, driving the map display as well as toggle UI
   * @param {String} ID - The layer ID to toggle
   */
  toggleLayer(id) {
    // Find the associated layer object
    const layer = this.getLayer(id)
    // Update the active state on the toggle button
    this.updateModeLayer(id)
    // Toggle layer on map
    layer.toggleGroup(!layer.active)

    // Update the preferences if toggling off since this is a user action
    if (!layer.active) {
      layer.updatePreferences(false)
    }

    // Slope Aspect and Slope Angle are mutually exclusive layers so we need toggle the opposite
    // off if one is being enabled
    const angleKey = 'slope_angle'
    const aspectKey = 'slope_aspect'
    if (id === angleKey && layer.active) {
      const aspectActive = this.getLayerPreference(aspectKey).active
      if (aspectActive) {
        this.toggleLayer(aspectKey)
      }
    } else if (id === aspectKey && layer.active) {
      const angleActive = this.getLayerPreference(angleKey)?.active
      if (angleActive) {
        this.toggleLayer(angleKey)
      }
    }
  }

  /**
   * Return the key for the layers preferences based on the app
   *
   * @returns {string} key for app specific layer preferences
   */
  getAppLayerKey() {
    return 'fishLayers'
  }

  /**
   * Get the layer preference object for a particular layer
   *
   * @param {string} id - layer id
   */
  getLayerPreference(id) {
    const layerSet = getItem(this.getAppLayerKey())
    const layerPref = layerSet?.find?.((layerPref) => layerPref.id === id)

    // If there is a preference saved in storage, return that
    if (layerPref) {
      return layerPref
    }

    return null
  }

  /**
   * Update layer preferences for a given layer
   *
   * @param {string} id - layer id
   * @param {bool} active - active state of the layer
   */
  updateLayerPreferences(id, active) {
    const layersKey = this.getAppLayerKey()
    const layerSet = getItem(layersKey)
    const layerPref = layerSet?.find?.((layerPref) => layerPref.id === id)

    // Save the layer's new active state
    if (layerPref) {
      layerPref.active = active
    }
    // Layer is new and default needs to be saved
    else {
      layerSet.push({ id: id, active: active })
    }

    // Update layer preference
    saveItem(layersKey, layerSet)
  }

  /**
   * Get the on/off preference for a particular collection
   * Specific to Hunt
   *
   * @param {string} code - collection unique_code
   */
  getCollectionPreference(code) {
    const collectionSet = getItem('layers-activeCollections')

    const active = collectionSet?.includes(code)
    return active
  }

  /**
   * Update collection preferences for a given collection
   * Specific to Hunt
   *
   * @param {string} code - collection unique_code
   * @param {bool} active - active state of the collection
   */
  updateCollectionPreferences(code, active) {
    let collectionSet = getItem('layers-activeCollections') || []
    const collectionActive = this.getCollectionPreference(code)

    if (collectionActive && !active) {
      // turn collection off
      collectionSet = collectionSet.filter((uniqueCode) => uniqueCode !== code)
    } else if (!collectionActive && active && !collectionSet.includes(code)) {
      // turn collection on
      collectionSet.push(code)
    }

    // Update collection preference
    saveItem('layers-activeCollections', collectionSet)
  }

  /**
   * Get the on/off preference for a particular hunt layer
   * Specific to Hunt
   *
   * @param {string} code - layer unique_code
   * @returns {Boolean} the user's saved active state of the layer, or null if there are no saved preferences
   */
  getHuntLayerPreference(code) {
    const layerSet = getItem('layers-activeLayers')

    if (!layerSet) {
      // return null if no layer preferences are saved
      return null
    }

    const active = layerSet?.includes(code.toString())
    return active
  }

  /**
   * Update layer preferences for a given hunt layer
   * Specific to Hunt
   *
   * @param {string} code - layer unique_code
   * @param {bool} active - active state of the layer
   */
  updateHuntLayerPreferences(code, active) {
    let layerSet = getItem('layers-activeLayers') || []
    const layerActive = this.getHuntLayerPreference(code)

    if (layerActive && !active) {
      // turn layer off
      layerSet = layerSet.filter((uniqueCode) => uniqueCode !== code.toString())
    } else if (!layerActive && active && !layerSet.includes(code)) {
      // turn layer on
      layerSet.push(code.toString())
    }

    // update layer preference
    saveItem('layers-activeLayers', layerSet)
  }

  /**
   * Request the app collections
   *
   * @return {JSON}
   */
  async requestCollections() {
    try {
      const data = await httpGetJson('/v2/collections')
      return data
    } catch {
      this.collectionsError = true
    }
  }

  /**
   * Request a collection's layers. For use when a collection only returns
   * the meta data from the request above - this is based on the app.
   *
   * @param {string} id - a collections unique id
   * @returns {JSON}
   */
  async requestLayers(id) {
    const data = await httpGetJson(`/v2/collections/${id}`)
    return data
  }

  /**
   * Adds JWT authentication to a given layer URL
   *
   * @param {string} url - layer url
   * @return {string} authenticated url
   */
  async addJWTAuth(url) {
    // FUTURE remove token from urls
    url += `?token=${await getAccessToken()}`
    return url
  }

  /**
   * Get if there was an error requesting collections
   * @returns {Boolean} if there was an error requesting collections
   */
  getCollectionsError() {
    return this.collectionsError
  }

  /**
   * Get the layer config so we can sort features on the map
   * @param {String} appName - app name to get the layer config for (only used for fish bc fish needs to request hunt layer config)
   * @returns {Object} layer config
   */
  async getLayerConfig() {
    if (!this.layerConfig) {
      const options = {
        method: 'GET',
      }

      const response = await fetch(`/config/fish-layers.json`, options)
      this.layerConfig = response.json()
    }

    return this.layerConfig
  }

  /**
   * Persist the full set of visible layers and their current active states
   * to local storage. This is used during the initial layer setup to ensure
   * we store a complete snapshot of the Backcountry layer state, rather than
   * just saving user toggled changes. This prevents mode-switch-related
   * fallback logic from overriding visibility after initial load.
   */
  persistAllLayerPreferences() {
    const key = this.getAppLayerKey()
    // Persist only toggleable layers with labels
    const allLayers = this.getModeLayers().filter((layer) => layer.label)

    const prefs = allLayers.map((layer) => ({
      id: layer.id,
      active: !!layer.active,
    }))

    saveItem(key, prefs)
  }
}

export default reactive(new LayerService())
